import { css, cva } from "styled-system/css";
import { pageContainerStyles } from "#/app.css";

// Spin animation keyframes
const spinKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Add keyframes to document if in browser
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = spinKeyframes;
  document.head.appendChild(style);
}

/* Hero Section */
export const compactHeroClass = css({
  display: "flex",
  flexDirection: "column",
  gap: "sm",
  marginBottom: "md",
});

export const coverWrapperClass = css({
  position: "relative",
  zIndex: 0,
  width: "100vw",
  marginInline: "calc(50% - 50vw)",
  aspectRatio: "2/3",
  maxHeight: "85vh",
  overflow: "hidden",
});

export const coverGradientClass = css({
  position: "absolute",
  insetInline: 0,
  bottom: 0,
  height: "45%",
  zIndex: 1,
  pointerEvents: "none",
  background: "linear-gradient(to top, {colors.bgBase200}, transparent)",
});

export const coverExpandBtnClass = css({
  position: "absolute",
  inset: 0,
  zIndex: 2,
  width: "100%",
  height: "100%",
  margin: 0,
  padding: 0,
  border: "none",
  backgroundColor: "transparent",
  cursor: "zoom-in",
});

export const heroCoverClass = css({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "top",
});

export const combinedInfoClass = css({
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  width: "100vw",
  marginInline: "calc(50% - 50vw)",
  marginTop: "-30vh",
  paddingTop: "1.25rem",
  paddingBottom: "xl",
  background:
    "linear-gradient(to bottom, color-mix(in srgb, {colors.bgBase200}, transparent 72%) 0%, color-mix(in srgb, {colors.bgBase200}, transparent 38%) 30%, {colors.bgBase200} 45%, {colors.bgBase200} 100%)",
  backdropFilter: "blur(16px)",
});

export const heroHeaderClass = css({
  display: "flex",
  flexDirection: "column",
  gap: "0.625rem",
});

export const bookTitleClass = css({
  fontFamily: "sans",
  fontWeight: "900",
  letterSpacing: "-0.025em",
  color: "textBase",
  fontSize: { base: "h2", md: "h1" },
  lineHeight: 1.25,
  margin: 0,
});

export const bookAuthorClass = css({
  fontSize: { base: "base", md: "lg" },
  fontWeight: "700",
  color: "textBase/60",
  margin: 0,
});

export const badgeGroupClass = css({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "sm",
  marginBottom: "sm",
});

export const tabContainerClass = css({
  display: "flex",
  gap: "1.5rem",
  marginTop: "0.625rem",
  borderBottomWidth: "1px",
  borderBottomStyle: "solid",
  borderBottomColor: "textBase/10",
});

export const tabButton = cva({
  base: {
    cursor: "pointer",
    transitionProperty: "color, font-weight",
    transitionDuration: "200ms",
    userSelect: "none",
    border: "none",
    outline: "none",
    flex: "0 0 auto",
    padding: "0.5rem 0",
    paddingLeft: 0,
    fontSize: "0.75rem",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    backgroundColor: "transparent",
    color: "textBase/40",
    borderBottomWidth: "2px",
    borderBottomStyle: "solid",
    borderBottomColor: "transparent",
  },
  variants: {
    active: {
      true: {
        fontWeight: "900",
        color: "primary",
        borderColor: "primary",
      },
    },
  },
});

export const descriptionTabContentClass = css({
  display: "flex",
  flexDirection: "column",
});

export const descriptionContentClass = css({
  paddingBlock: "md",
  fontSize: "sm",
  lineHeight: 1.625,
  color: "textBase/80",
});

export const tagsContainerClass = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "0.375rem",
  marginTop: "md",
});

export const tagClass = css({
  display: "inline-block",
  padding: "0.25rem 0.625rem",
  fontSize: "tiny",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderRadius: "0.2rem",
  transitionProperty: "background-color",
  transitionDuration: "150ms",
  cursor: "default",
});

export const genreVariantClass = css({
  backgroundColor: "primary/10",
  color: "primary",
});

export const tagVariantClass = css({
  backgroundColor: "bgBase300",
  color: "textBase/40",
});

export const resumeButtonContainerClass = css({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  borderRadius: "medium",
  backgroundColor: "primary",
  boxShadow: "md",
  transitionProperty: "transform, box-shadow",
  transitionDuration: "200ms",
  cursor: "pointer",
  _active: {
    transform: "scale(0.98)",
    boxShadow: "sm",
  },
});

export const resumeButtonMainClass = css({
  display: "flex",
  margin: 0,
  padding: "0.5rem 0.75rem",
  flex: 1,
  minWidth: 0,
  alignItems: "center",
  gap: "0.25rem",
  cursor: "pointer",
  userSelect: "none",
  borderStyle: "solid",
  borderColor: "transparent",
  color: "primaryContent",
  _disabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

export const resumeButtonTextClass = css({
  fontSize: "xs",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
});

export const resumeButtonChapterClass = css({
  fontSize: "10px",
  fontWeight: "700",
  opacity: 0.8,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const resumeButtonDropdownClass = css({
  cursor: "pointer",
  userSelect: "none",
  borderWidth: 0,
  borderStyle: "solid",
  borderColor: "transparent",
  outline: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 2rem",
  flexShrink: 0,
  padding: 0,
  margin: 0,
  backgroundColor: "transparent",
  color: "primaryContent",
  borderLeftWidth: "1px",
  borderLeftColor: "primaryContent/30",
});

export const statItemClass = css({
  display: "flex",
  flexDirection: "column",
  gap: "0.125rem",
});

export const statLabelClass = css({
  fontSize: "9px",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "textBase/40",
});

export const statValueCompactClass = css({
  fontSize: "sm",
  fontWeight: "900",
  color: "textBase",
});

export const actionMenuContentClass = css({
  position: "absolute",
  zIndex: 100,
  padding: "sm",
  backgroundColor: "bgBase100",
  borderRadius: "medium",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "textBase/10",
  boxShadow: "xl",
  minWidth: "10rem",
  top: "100%",
  right: 0,
  marginTop: "0.25rem",
});

/* Dialog */
export const dialogOverlayClass = css({
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "md",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  backdropFilter: "blur(4px)",
});

export const dialogBoxClass = css({
  width: "100%",
  maxWidth: "28rem",
  overflow: "hidden",
  backgroundColor: "bgBase100",
  borderRadius: "xlarge",
  boxShadow: "2xl",
});

export const dialogHeaderClass = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "md",
  borderBottomWidth: "1px",
  borderBottomStyle: "solid",
  borderBottomColor: "textBase/10",
});

export const dialogTitleClass = css({
  fontSize: "h3",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "-0.025em",
  marginBlock: 0,
});

export const dialogBodyClass = css({
  padding: "lg",
  display: "flex",
  flexDirection: "column",
  gap: "lg",
});

export const dialogLabelClass = css({
  fontSize: "xs",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "textBase/40",
});

/* Method Toggle */
export const methodContainerClass = css({
  display: "flex",
  flexDirection: "column",
  gap: "xs",
});

export const methodToggleClass = css({
  display: "flex",
  backgroundColor: "bgBase200",
  borderRadius: "0.75rem",
  padding: "0.25rem",
});

export const methodButton = cva({
  base: {
    flex: 1,
    paddingX: "md",
    paddingY: "sm",
    fontSize: "12px",
    fontWeight: "900",
    textTransform: "uppercase",
    borderRadius: "0.5rem",
    transitionProperty: "all",
    transitionDuration: "200ms",
    backgroundColor: "transparent",
    color: "inherit",
    opacity: 0.5,
  },
  variants: {
    active: {
      true: {
        backgroundColor: "bgBase100",
        color: "primary",
        opacity: 1,
        boxShadow: "xs",
      },
    },
    disabled: {
      true: {
        opacity: 0.2,
        cursor: "not-allowed",
      },
    },
  },
});

/* Chapter List */
export const chapterListContainerClass = css({
  overflow: "hidden",
});

export const chapterListHeaderClass = css({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  paddingX: { base: "sm", md: "lg" },
  paddingY: { base: "sm", md: "md" },
  backgroundColor: "bgBase200/50",
  borderBottomWidth: "1px",
  borderBottomStyle: "solid",
  borderBottomColor: "textBase/10",
});

export const sortBtnClass = css({
  cursor: "pointer",
  transitionProperty: "all",
  transitionDuration: "200ms",
  userSelect: "none",
  border: "none",
  outline: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "sm",
  padding: "0.25rem 0.5rem",
  fontSize: "xs",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  opacity: 0.6,
  color: "textBase",
  backgroundColor: "transparent",
  borderRadius: "small",
  _hover: {
    opacity: 1,
    backgroundColor: "textBase/5",
  },
});

export const chapterListScrollClass = css({});

export const chapterGridClass = css({
  display: "grid",
  gridTemplateColumns: { base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
  columnGap: "md",
  rowGap: "xs",
});

export const chapterItemClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "2.25rem",
  borderRadius: "0.75rem",
  transitionProperty: "background-color",
  transitionDuration: "200ms",
  _hover: {
    backgroundColor: "bgBase200/50",
  },
});

export const chapterTitleClass = css({
  fontSize: "sm",
  fontWeight: "700",
  color: "inherit",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flex: 1,
  _hover: {
    color: "primary",
  },
});

export const emptyChaptersClass = css({
  paddingY: "xxl",
  textAlign: "center",
  fontWeight: "700",
  fontStyle: "italic",
  color: "textBase/20",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  fontSize: "sm",
});

/* List Footer */
export const listFooterClass = css({
  gridColumn: "1 / -1",
  paddingY: "md",
  textAlign: "center",
  fontSize: "12px",
  fontWeight: "900",
  textTransform: "uppercase",
  color: "primary",
  opacity: 0.5,
});

/* Badges */
export const sourceBadgeClass = css({
  padding: "0.125rem 0.375rem",
  fontSize: "9px",
  fontWeight: "900",
  backgroundColor: "primary/10",
  color: "primary",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "primary/20",
  borderRadius: "4px",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
});

export const badgeBaseClass = css({
  padding: "0.125rem 0.375rem",
  fontSize: "9px",
  fontWeight: "900",
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "4px",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
});

export const badgeStatusCompletedClass = css({
  backgroundColor: "success/10",
  color: "success",
  borderColor: "success/20",
});

export const badgeStatusOngoingClass = css({
  backgroundColor: "info/10",
  color: "info",
  borderColor: "info/20",
});

export const badgeStatusHiatusClass = css({
  backgroundColor: "warning/10",
  color: "warning",
  borderColor: "warning/20",
});

export const badgeStatusDroppedClass = css({
  backgroundColor: "error/10",
  color: "error",
  borderColor: "error/20",
});

export const badgeStatusUnknownClass = css({
  backgroundColor: "bgBase300",
  color: "textBase/40",
  borderColor: "textBase/20",
});

export const badgeDownloadFullClass = css({
  backgroundColor: "primary/10",
  color: "primary",
  borderColor: "primary/20",
});

export const badgeDownloadPartialClass = css({
  backgroundColor: "bgBase300",
  color: "textBase/40",
  borderColor: "textBase/20",
});

export const badgeDownloadUnknownClass = css({
  backgroundColor: "bgBase100",
  color: "textBase/30",
  borderColor: "textBase/10",
});

/* Download Badge Button */
export const downloadBadgeBtnClass = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "xs",
  padding: "0.25rem 0.5rem",
  fontSize: "9px",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  borderRadius: "4px",
  backgroundColor: "primary/10",
  color: "primary",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "primary/20",
  cursor: "pointer",
  transitionProperty: "all",
  transitionDuration: "200ms",
  textDecoration: "none",
  _hover: {
    backgroundColor: "primary",
    color: "white",
  },
});

export const spinClass = css({
  animation: "spin 1s linear infinite",
});

/* Cover Full-Screen Preview */
export const coverOverlayClass = css({
  position: "fixed",
  inset: 0,
  zIndex: 200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "color-mix(in srgb, black 85%, transparent)",
});

export const coverOverlayImgClass = css({
  width: "100%",
  height: "100%",
  objectFit: "contain",
});
